import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useMeta } from "@/hooks/useMeta";
import { PageHeader, FilterCard, Field, SimpleSelect, EmptyState } from "@/components/common";
import { fmtPct, statusBg, MONTHS } from "@/lib/helpers";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Fragment } from "react";
import { Layers, ChevronRight, ChevronDown } from "lucide-react";

export default function RekapDepartemen() {
  const { indikators, tahuns } = useMeta();
  const [indikator, setIndikator] = useState("");
  const [tahun, setTahun] = useState("");
  const [bulan, setBulan] = useState("__all__");
  const [data, setData] = useState(null);
  const [open, setOpen] = useState({});

  useEffect(() => { if (!indikator && indikators.length) setIndikator(indikators[0].kode); }, [indikators]);
  useEffect(() => { if (!tahun && tahuns.length) setTahun(String(tahuns[0])); }, [tahuns]);

  useEffect(() => {
    if (!indikator || !tahun) return;
    let url = `/rekap/departemen?indikator=${encodeURIComponent(indikator)}&tahun=${tahun}`;
    if (bulan !== "__all__") url += `&bulan=${bulan}`;
    api.get(url).then((r) => setData(r.data));
  }, [indikator, tahun, bulan]);

  const toggle = (d) => setOpen((o) => ({ ...o, [d]: !o[d] }));

  return (
    <div data-testid="rekap-departemen-page">
      <PageHeader icon={Layers} title="Rekap per Departemen" subtitle="Agregat N & D seluruh poliklinik dalam satu departemen. Capaian = total N / total D. Klik baris untuk detail." />

      <FilterCard>
        <Field label="Indikator">
          <SimpleSelect testId="rd-indikator-select" value={indikator} onChange={setIndikator}
            options={indikators.map((i) => ({ label: i.kode, value: i.kode }))} className="w-[220px]" />
        </Field>
        <Field label="Tahun">
          <SimpleSelect testId="rd-tahun-select" value={tahun} onChange={setTahun}
            options={tahuns.map((t) => ({ label: String(t), value: t }))} className="w-[120px]" />
        </Field>
        <Field label="Bulan">
          <SimpleSelect testId="rd-bulan-select" value={bulan} onChange={setBulan}
            options={[{ label: "Sepanjang Tahun", value: "__all__" }, ...MONTHS.map((m) => ({ label: m.nama, value: m.n }))]} className="w-[180px]" />
        </Field>
      </FilterCard>

      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-0">
          {!data || data.departemen.length === 0 ? (
            <EmptyState icon={Layers} title="Belum ada data" desc="Belum ada capaian untuk filter ini." />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="w-8"></TableHead>
                    <TableHead>Departemen / Poliklinik</TableHead>
                    <TableHead className="text-right">Total N</TableHead>
                    <TableHead className="text-right">Total D</TableHead>
                    <TableHead className="text-right">Target</TableHead>
                    <TableHead className="text-right">Capaian</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.departemen.map((dep) => (
                    <Fragment key={dep.nama_departemen}>
                      <TableRow key={dep.nama_departemen} className="cursor-pointer hover:bg-slate-50 font-semibold" onClick={() => toggle(dep.nama_departemen)} data-testid={`dept-row-${dep.nama_departemen}`}>
                        <TableCell>{open[dep.nama_departemen] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</TableCell>
                        <TableCell>{dep.nama_departemen}</TableCell>
                        <TableCell className="text-right tabular-nums">{dep.total_n}</TableCell>
                        <TableCell className="text-right tabular-nums">{dep.total_d}</TableCell>
                        <TableCell className="text-right">—</TableCell>
                        <TableCell className="text-right"><Badge className="tabular-nums">{fmtPct(dep.capaian)}</Badge></TableCell>
                      </TableRow>
                      {open[dep.nama_departemen] && dep.poliklinik.map((p) => (
                        <TableRow key={p.poliklinik_id} className="bg-slate-50/50" data-testid={`poli-detail-${p.poliklinik_id}`}>
                          <TableCell></TableCell>
                          <TableCell className="pl-8 text-slate-600">{p.nama_poliklinik}</TableCell>
                          <TableCell className="text-right tabular-nums">{p.total_n}</TableCell>
                          <TableCell className="text-right tabular-nums">{p.total_d}</TableCell>
                          <TableCell className="text-right tabular-nums text-slate-500">{fmtPct(p.target)}</TableCell>
                          <TableCell className="text-right">
                            <span className={`inline-block px-2 py-1 rounded-md text-xs font-semibold tabular-nums ${statusBg(p.capaian, p.target) || "bg-slate-100 text-slate-500"}`}>{fmtPct(p.capaian)}</span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </Fragment>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
